import type { AnalyticsProduct } from "@/common/contracts/analytics.contracts";
import type { SearchResponse } from "@/features/search/api/queries";
import type { ProductCardProduct } from "@/features/search/components/product-card/product-card.context";

/**
 * A search result in the shape the analytics layer expects.
 *
 * The events are the gateway's, so `productToAnalyticsItem` reads the gateway's product: nested
 * `auction`, snake_case, taxonomy objects. The search service returns a flatter DTO built for
 * rendering a card, so this maps across. `AnalyticsProduct` is a deep-partial, which is what makes
 * a subset legal.
 *
 * Fields the new DTO simply doesn't carry, and which therefore go out undefined:
 * `item_brand` and `item_category*` (the DTO has ids, not slugs — facets carry the labels),
 * `item_views`, `item_favorites`, `item_status`, `item_country_of_seller`, `item_seller_review`,
 * `item_publish_date`, `item_designer`. Adding any of them means adding it to `/search` first;
 * inventing a value here would be worse than the gap.
 */
export const toAnalyticsProduct = (
  product: ProductCardProduct
): AnalyticsProduct => ({
  id: product.id,
  title: product.title,
  number_of_items: product.numberOfItems ?? undefined,
  is_in_showroom: product.labels?.showroom ?? undefined,
  images: product.images?.map(image => ({ url: image.url })),
  auction: {
    /* Amount only — the mapper reads `.amount`, and the gateway types `currency` as an enum this
     * DTO's plain string wouldn't satisfy. `discount` is derived from these two by `getPriceInfo`. */
    buy_now_price: product.buyNowPrice
      ? { amount: product.buyNowPrice }
      : undefined,
    /* Bid-eligible is `bidFrom != null` here; the gateway exposed it as its own flag. */
    allow_bid: product.bidFrom != null,
  },
  original_price: product.originalPrice
    ? { amount: product.originalPrice }
    : undefined,
});

/**
 * The list a result was clicked in — the category being browsed, or `search_results` for a plain
 * query. Mirrors the gateway PLP, which used the route's category slug and the same fallback, so
 * the two implementations report into one bucket rather than splitting the funnel in GA.
 *
 * `listId` and `listName` are deliberately the same value: that is what the old page sent.
 */
export const getSearchListId = (data: SearchResponse | undefined): string =>
  data?.meta.taxonomy.path.at(-1)?.slug ?? "search_results";
